import React from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import "mdb-ui-kit/css/mdb.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { Input, Ripple, initMDB } from "mdb-ui-kit";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useMediaQuery, useTheme } from "@mui/material";
import FooterManual from "./footerManual";
import Notes from "./header";

initMDB({ Input, Ripple, initMDB });

// Constants for Color Palette - RED THEME
const RED = "#DC2626";
const DARK_RED_ACCENT = "#991B1B";
const BLACK_TEXT = "#1F2937";
const GRAY_TEXT = "#4B5563";
const LIGHT_RED_BG = "#FEF2F2";
const EXTRA_LIGHT_RED_HOVER = "#FFF5F5";

// Styled Paper Component
const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
    ...theme.applyStyles("dark", {
        backgroundColor: "#1A2027",
    }),
}));

// Helper component for creating the data cards
const InfoCard = ({ title, icon, children }) => (
    <Box 
        sx={{ 
            border: `2px solid ${DARK_RED_ACCENT}`, 
            marginTop: '20px',
            borderRadius: "10px",
            overflow: "hidden",
            boxShadow: `0 4px 12px rgba(220, 38, 38, 0.15)`, 
            transition: "transform 0.3s ease-in-out",
            '&:hover': {
                transform: "translateY(-5px)",
                boxShadow: `0 10px 20px rgba(220, 38, 38, 0.25)`,
            }
        }}
    >
        <Box 
            sx={{ 
                backgroundColor: RED, 
                color: "white", 
                padding: "15px 20px",
                borderBottom: `2px solid ${DARK_RED_ACCENT}` 
            }}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", display: 'flex', alignItems: 'center' }}>
                <i className={`fas fa-${icon}`} style={{ marginRight: '10px' }}></i>
                {title}
            </Typography>
        </Box>
        <Box sx={{ padding: "20px", backgroundColor: "#fff" }}>
            {children}
        </Box>
    </Box>
);

// Helper component for styled list/detail items
const DetailItem = ({ label, value, labelColor, hoverColor }) => (
    <Box 
        sx={{ 
            display: "flex", 
            alignItems: "flex-start",
            padding: "10px 8px",
            borderBottom: "1px solid #E5E7EB",
            '&:last-child': { borderBottom: 'none' },
            '&:hover': { backgroundColor: hoverColor || EXTRA_LIGHT_RED_HOVER }, 
            transition: 'background-color 0.2s'
        }}
    >
        <Typography sx={{ 
            fontWeight: "600", 
            minWidth: { xs: "110px", sm: "140px" }, 
            color: labelColor || RED, 
            fontSize: "14px",
            flexShrink: 0
        }}>
            {label}:
        </Typography>
        <Typography sx={{ color: BLACK_TEXT, flex: 1, fontSize: "14px", lineHeight: "1.4", whiteSpace: "pre-wrap" }}>
            {value}
        </Typography>
    </Box>
);

// New component for payment method cards
const PaymentMethodCard = ({ title, icon, fee, limit, notes }) => (
    <Box 
        sx={{ 
            border: `1px solid ${RED}`, 
            borderRadius: "8px",
            padding: "20px",
            backgroundColor: LIGHT_RED_BG,
            marginBottom: "15px",
            transition: "all 0.3s ease",
            '&:hover': {
                backgroundColor: EXTRA_LIGHT_RED_HOVER,
                boxShadow: `0 4px 12px rgba(220, 38, 38, 0.15)`,
            }
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <i className={`fas fa-${icon}`} style={{ color: RED, fontSize: '24px', marginRight: '10px' }}></i>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: BLACK_TEXT }}>
                {title}
            </Typography>
        </Box>
        <Box sx={{ marginBottom: '10px' }}>
            <Typography sx={{ color: DARK_RED_ACCENT, fontWeight: 'bold', fontSize: '14px' }}>
                Limit: {limit}
            </Typography>
            <Typography sx={{ color: DARK_RED_ACCENT, fontWeight: 'bold', fontSize: '14px' }}>
                Fee: {fee}
            </Typography>
        </Box>
        <Typography sx={{ color: GRAY_TEXT, fontSize: '14px' }}>
            {notes}
        </Typography>
    </Box>
);

const OfficialBankDetails = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

    return (
        <Box
            className="container-main-AllSeeModels"
            sx={{
                position: "relative",
                width: "100%",
                minHeight: "100vh",
                backgroundColor: "#fff"
            }}
        >
            {/* Header/Notes Component */}
            <Box sx={{ marginBottom: "0%" }}><Notes /></Box>

            {/* Banner Section */}
            <Box
                className="image-container-AllSeeModels"
                sx={{
                    position: "relative",
                    width: "100%",
                    height: isMobile ? "25vh" : isTablet ? "35vh" : "50vh",
                    backgroundColor: "black",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: "hidden",
                }}
            >
                <Box
                    component="img"
                    src="/image/AllSeeModelsBackgroungCar.png" 
                    alt="Automotive Background"
                    sx={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        opacity: 0.35,
                    }}
                />
                

                <Typography
                    sx={{
                        position: "relative",
                        color: "white",
                        fontWeight: "900",
                        textTransform: "uppercase",
                        letterSpacing: isMobile ? "1px" : isTablet ? "2px" : "4px",
                        fontSize: isMobile ? "24px" : isTablet ? "42px" : "60px",
                        zIndex: 2,
                        textShadow: "0 0 10px rgba(0,0,0,0.8)",
                        textAlign: "center",
                        lineHeight: 1.1,
                        px: "20px",
                    }}
                >
                    Official Payment Details
                </Typography>
            </Box>

            {/* Main Content Container */}
            <Box
                className="logo-section-AllSeeModels"
                sx={{
                    width: isMobile ? "95%" : isTablet ? "92%" : "85%",
                    backgroundColor: "#FFFFFF",
                    margin: "0 auto",
                    marginTop: isMobile ? "-50px" : isTablet ? "-60px" : "-80px",
                    borderRadius: isMobile ? "12px" : isTablet ? "16px" : "20px",
                    padding: isMobile ? "20px 16px" : isTablet ? "32px 24px" : "40px 30px",
                    boxShadow: "0 15px 50px rgba(0, 0, 0, 0.12)",
                    zIndex: 10,
                    position: "relative",
                }}
            >
                
                {/* Introduction */}
                <Box sx={{ paddingBottom: "30px", borderBottom: `2px solid ${RED}`, marginBottom: "30px" }}>
                    <Typography 
                        variant="h5" 
                        sx={{ 
                            fontWeight: "700", 
                            color: BLACK_TEXT, 
                            marginBottom: "10px",
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: isMobile ? "18px" : isTablet ? "24px" : "32px",
                        }}
                    >
                        <i className="fas fa-credit-card" style={{ color: RED, marginRight: '10px' }}></i>
                        Secure Payment Instructions
                    </Typography>
                    <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "13px" : isTablet ? "14px" : "15px", lineHeight: "1.6" }}>
                        All payments for New & Used Cars, Auto Parts, Scraps, and auction deposits must be remitted to one of our official bank accounts listed below.
                    </Typography>
                    <Typography sx={{ color: RED, fontWeight: 'bold', fontSize: isMobile ? "12px" : isTablet ? "13px" : "14px", marginTop: "10px" }}>
                        Important: Always reference your Pro-forma Invoice for the specific account and exact amount required.
                    </Typography>
                </Box>

                {/* Primary Accounts Section */}
                <InfoCard title="Primary Accounts for International Wire Transfer (T/T)" icon="university">
                    <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "13px" : isTablet ? "14px" : "15px", marginBottom: "15px" }}>
                        We recommend using the designated currency account to avoid excessive exchange rate fees levied by intermediary banks.
                    </Typography>
                    
                    {/* JPY Account */}
                    <Box sx={{ marginBottom: "25px" }}>
                        <Typography variant="h6" sx={{ color: DARK_RED_ACCENT, fontWeight: 'bold', marginBottom: '15px', borderBottom: `1px solid ${LIGHT_RED_BG}`, paddingBottom: '5px', fontSize: isMobile ? "14px" : "18px" }}>
                            Japanese Yen (JPY) Account
                        </Typography>
                        <Box sx={{ display: isMobile ? "block" : "flex", flexDirection: isMobile ? "column" : "row" }}>
                            <Box sx={{ marginBottom: isMobile ? "15px" : "0", flex: 1 }}>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>Bank Name</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "12px", lineHeight: "1.4" }}>BANK-SUMITO MITSUBUSHI BANKING CORPORATION</Typography>
                                
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>Branch Name</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "12px", lineHeight: "1.4" }}>BRANCH-KASUKABE BRANCH</Typography>
                                
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>SWIFT/BIC Code</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "12px", lineHeight: "1.4" }}>SMBC-JPJT</Typography>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>Account Name</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "12px", lineHeight: "1.4" }}>AMAR ENTERPRISE CO. LTD</Typography>
                                
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>Account Number</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "12px", lineHeight: "1.4" }}>7160389</Typography>
                                
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>Bank Address</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>1076-1 Ooaza Komizou Iwatsuki Saitama - Shi, 339-0003, Japan</Typography>
                            </Box>
                        </Box>
                    </Box>
                    
                    {/* USD Account */}
                    <Box sx={{ marginBottom: "25px" }}>
                        <Typography variant="h6" sx={{ color: DARK_RED_ACCENT, fontWeight: 'bold', marginBottom: '15px', borderBottom: `1px solid ${LIGHT_RED_BG}`, paddingBottom: '5px', fontSize: isMobile ? "14px" : "18px" }}>
                            US Dollar (USD) Account
                        </Typography>
                        <Box sx={{ display: isMobile ? "block" : "flex", flexDirection: isMobile ? "column" : "row" }}>
                            <Box sx={{ marginBottom: isMobile ? "15px" : "0", flex: 1 }}>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>Bank Name</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "12px", lineHeight: "1.4" }}>BANK-SUMITO MITSUBUSHI BANKING CORPORATION</Typography>
                                
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>Branch Name</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "12px", lineHeight: "1.4" }}>BRANCH- KASUKABE BRANCH</Typography>
                                
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>SWIFT/BIC Code</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "12px", lineHeight: "1.4" }}>SMBC-JPJT</Typography>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>Account Name</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "12px", lineHeight: "1.4" }}>AMAR ENTERPRISE CO. LTD</Typography>
                                
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>Account Number</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "12px", lineHeight: "1.4" }}>0114357</Typography>
                                
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>Bank Address</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>1076-1 Ooaza Komizou Iwatsuki Saitama - Shi, 339-0003, Japan</Typography>
                            </Box>
                        </Box>
                    </Box>
                    
                    {/* EUR Account */}
                    {/* <Box sx={{ marginBottom: "10px" }}>
                        <Typography variant="h6" sx={{ color: DARK_RED_ACCENT, fontWeight: 'bold', marginBottom: '15px', borderBottom: `1px solid ${LIGHT_RED_BG}`, paddingBottom: '5px', fontSize: isMobile ? "14px" : "18px" }}>
                            Euro (EUR) Account
                        </Typography>
                        <Box sx={{ display: isMobile ? "block" : "flex", flexDirection: isMobile ? "column" : "row" }}>
                            <Box sx={{ marginBottom: isMobile ? "15px" : "0", flex: 1 }}>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>Bank Name</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "12px", lineHeight: "1.4" }}>Placeholder Bank Name (e.g., Mizuho Bank, Ltd.)</Typography>
                                
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>Branch Name</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "12px", lineHeight: "1.4" }}>Placeholder Branch Name (e.g., International Remittance Div.)</Typography>
                                
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>SWIFT/BIC Code</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "12px", lineHeight: "1.4" }}>PHSWIFTEUR</Typography>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>Account Name</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "12px", lineHeight: "1.4" }}>Amar Enterprises Co Ltd</Typography>
                                
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>Account Number</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "12px", lineHeight: "1.4" }}>1122334455 (Placeholder)</Typography>
                                
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px" }}>Bank Address</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>1076-1 Ooaza Komizou Iwatsuki Saitama - Shi, 339-0003, Japan</Typography>
                            </Box>
                        </Box>
                    </Box> */}
                </InfoCard>
                
                {/* Digital Payment Options */}
                <InfoCard title="Digital and Alternative Payment Options" icon="digital-tachograph">
                    <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "13px" : isTablet ? "14px" : "15px", marginBottom: "15px" }}>
                        These options are generally reserved for deposits or lower-value goods (Auto Parts, Scraps), as per our Payment Terms.
                    </Typography>
                    
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "15px" }}>
                        {/* <PaymentMethodCard 
                            title="PayPal"
                            icon="paypal"
                            limit="Payments under US$ 7,000 equivalent"
                            fee="4-6% commission fee added to invoice"
                            notes="Commission fee is added to the invoice and borne by the customer."
                        /> */}
                        
                        <PaymentMethodCard 
                            title="Credit/Debit Card"
                            icon="credit-card"
                            limit="Processed via secure third-party payment gateway"
                            fee="3-5% processing fee added to invoice"
                            notes="Processing fee is added to the invoice and borne by the customer."
                        />
                    </Box>
                    
                    <Box sx={{ marginTop: "15px", padding: "15px", backgroundColor: LIGHT_RED_BG, borderRadius: "5px" }}>
                        <Typography sx={{ color: RED, fontWeight: 'bold', fontSize: "14px", display: 'flex', alignItems: 'center' }}>
                            <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
                            Important: We DO NOT accept personal checks or Money Gram.
                        </Typography>
                    </Box>
                </InfoCard>
                
                {/* Payment Guidelines */}
                <InfoCard title="Mandatory Payment Remittance Guide" icon="list-check">
                    <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "13px" : isTablet ? "14px" : "15px", marginBottom: "15px" }}>
                        To ensure your funds are received promptly and to prevent delays in shipping, please follow these guidelines:
                    </Typography>
                    
                    <Box sx={{ marginBottom: "15px" }}>
                        <Box sx={{ marginBottom: "15px", paddingBottom: "15px", borderBottom: `1px solid #E5E7EB` }}>
                            <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "8px" }}>
                                1. Reference Mandate
                            </Typography>
                            <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.5" }}>
                                You must include the Pro-forma Invoice Number and/or the Vehicle Chassis Number in the message or description field of your bank transfer. Failure to do so will delay payment confirmation.
                            </Typography>
                        </Box>

                        <Box sx={{ marginBottom: "15px", paddingBottom: "15px", borderBottom: `1px solid #E5E7EB` }}>
                            <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "8px" }}>
                                2. Bank Charges (Sender Pays All)
                            </Typography>
                            <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.5" }}>
                                You must instruct your bank to use the "OUR" option, meaning the Sender (Customer) pays all bank charges (sending, intermediary, and receiving fees). The full invoice amount must be received in our account. Any shortfall will be invoiced immediately.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "8px" }}>
                                3. No Intermediary Risk
                            </Typography>
                            <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.5" }}>
                                Once funds leave your account, they are subject to intermediary bank transfer risks. Amar Enterprises Co Ltd is only responsible for the amount received and cleared in our beneficiary account.
                            </Typography>
                        </Box>
                    </Box>
                </InfoCard>

                {/* Security Section */}
                <InfoCard title="Security and Fraud Prevention" icon="shield-alt">
                    <Box sx={{ padding: "15px", backgroundColor: LIGHT_RED_BG, borderRadius: "5px", marginBottom: "15px" }}>
                        <Typography variant="h6" sx={{ color: DARK_RED_ACCENT, fontWeight: 'bold', marginBottom: '10px' }}>
                            Security Guarantee
                        </Typography>
                        <Typography sx={{ color: BLACK_TEXT, fontSize: "14px", lineHeight: "1.6" }}>
                            We will NEVER change our bank account details via email, text message, or phone call without prior written notification on our official company letterhead and confirmed via a secondary contact method.
                        </Typography>
                    </Box>
                    
                    <Box sx={{ padding: "15px", border: `1px solid ${RED}`, borderRadius: "5px" }}>
                        <Typography sx={{ color: RED, fontWeight: 'bold', fontSize: "14px", display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                            Important Security Notice
                        </Typography>
                        <Typography sx={{ color: BLACK_TEXT, fontSize: "14px" }}>
                            If you receive any unverified instruction to pay a different account, stop the transfer immediately and contact us by phone at <span style={{ fontWeight: 'bold', color: RED }}>+81-48-793-3911</span>.
                        </Typography>
                    </Box>
                </InfoCard>

            </Box>
            
            {/* Space Filler for Footer Alignment */}
            <Box sx={{ height: isMobile ? "10px" : isTablet ? "10px" : "50px" }} />
               {/* Space Filler for Footer Alignment */}
            {/* <Box sx={{ height: isMobile ? "10px" : isTablet ? "10px" : "50px" }} /> */}
            
            {/* Hidden Stamp Image - Only visible in inspector */}
            <Box
                component="img"
                src="/image/stamp.png"
                alt="Official Stamp"
                sx={{
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    opacity: 0,
                    pointerEvents: 'none',
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                }}
            />
            
            {/* Footer Component */}
            <FooterManual />
        </Box>
    );
};

export default OfficialBankDetails;